import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Persistent desktop header search with focus suggestions + predictive results.
 */
class InlineSearchComponent extends Component {
  requiredRefs = ['searchInput', 'searchContainer', 'clearButton', 'predictiveSearchResults', 'placeholder', 'placeholderTerm'];

  #controller = new AbortController();
  #activeFetch = null;
  #placeholderTimer = null;
  #placeholderIndex = 0;
  #placeholders = [];
  #isAnimating = false;
  #sectionId = 'predictive-search';

  connectedCallback() {
    super.connectedCallback();

    if (window.innerWidth < 1024) {
      return;
    }

    const { signal } = this.#controller;
    const input = this.refs.searchInput;
    const clearButton = this.refs.clearButton;

    input?.addEventListener('input', this.#handleInput, { signal });
    input?.addEventListener('input', debounce(this.#handleSearch, 300), { signal });
    input?.addEventListener('keydown', this.#handleKeyDown, { signal });
    input?.addEventListener('focus', this.#handleFocus, { signal });
    input?.addEventListener('blur', this.#onBlur, { signal });
    clearButton?.addEventListener('mousedown', this.#preventBlur, { signal });
    clearButton?.addEventListener('click', this.#handleClearClick, { signal });
    this.refs.suggestionPills?.addEventListener('mousedown', this.#preventBlur, { signal });
    this.refs.suggestionPills?.addEventListener('click', this.#handleSuggestionClick, { signal });
    this.refs.collectionPrev?.addEventListener('mousedown', this.#preventBlur, { signal });
    this.refs.collectionNext?.addEventListener('mousedown', this.#preventBlur, { signal });
    this.refs.collectionPrev?.addEventListener('click', () => this.#scrollCollection(-1), { signal });
    this.refs.collectionNext?.addEventListener('click', () => this.#scrollCollection(1), { signal });
    this.refs.collectionTrack?.addEventListener('mousedown', this.#preventBlur, { signal });
    this.refs.collectionTrack?.addEventListener('scroll', this.#updateCollectionNav, { signal, passive: true });
    this.refs.suggestionsPanel?.addEventListener('mousedown', (event) => {
      // Allow product links to navigate; keep focus for other panel interactions
      if (!(event.target instanceof Element && event.target.closest('a'))) {
        this.#preventBlur(event);
      }
    }, { signal });
    document.addEventListener('click', this.#handleClickOutside, { signal });
    document.addEventListener('keydown', this.#handleEscapeKey, { signal });

    this.#initPlaceholders();
    this.#updateClearButton();
    this.#updateCollectionNav();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopPlaceholderRotation();
    this.#controller.abort();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
  }

  #initPlaceholders = () => {
    const input = this.refs.searchInput;
    if (!input) return;

    try {
      const raw = input.getAttribute('data-placeholders');
      this.#placeholders = raw ? JSON.parse(raw) : [];
    } catch {
      this.#placeholders = [];
    }

    if (this.#placeholders.length > 0) {
      this.#setTermText(this.#placeholders[0]);
      this.#startPlaceholderRotation();
    }
  };

  #setTermText = (term) => {
    const termEl = this.refs.placeholderTerm;
    if (!termEl) return;
    termEl.textContent = `"${term}"`;
  };

  #rotatePlaceholder = () => {
    if (
      this.#isAnimating ||
      this.#placeholders.length < 2 ||
      document.activeElement === this.refs.searchInput ||
      this.refs.searchInput?.value
    ) {
      return;
    }

    if (prefersReducedMotion()) {
      this.#placeholderIndex = (this.#placeholderIndex + 1) % this.#placeholders.length;
      this.#setTermText(this.#placeholders[this.#placeholderIndex]);
      return;
    }

    const current = this.refs.placeholderTerm;
    const track = current?.parentElement;
    if (!track || !current) return;

    this.#isAnimating = true;
    this.#placeholderIndex = (this.#placeholderIndex + 1) % this.#placeholders.length;
    const nextTerm = this.#placeholders[this.#placeholderIndex];

    const next = document.createElement('span');
    next.className = 'inline-search__placeholder-term';
    next.textContent = `"${nextTerm}"`;
    track.appendChild(next);

    void next.offsetWidth;

    current.classList.remove('is-active');
    current.classList.add('is-exit');
    next.classList.add('is-active');

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      current.remove();
      next.setAttribute('ref', 'placeholderTerm');
      this.#isAnimating = false;
    };

    next.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform') finish();
    }, { once: true });
    window.setTimeout(finish, 550);
  };

  #startPlaceholderRotation = () => {
    this.#stopPlaceholderRotation();
    if (this.#placeholders.length < 2) return;

    this.#placeholderTimer = window.setInterval(() => {
      this.#rotatePlaceholder();
    }, 2800);
  };

  #stopPlaceholderRotation = () => {
    if (this.#placeholderTimer) {
      window.clearInterval(this.#placeholderTimer);
      this.#placeholderTimer = null;
    }
  };

  #handleFocus = () => {
    this.#stopPlaceholderRotation();
    this.#updateClearButton();
    this.#syncPanels();
  };

  #onBlur = () => {
    requestAnimationFrame(() => {
      if (this.contains(document.activeElement)) return;

      this.#updateClearButton();
      this.#hideSuggestions();
      if (!this.refs.searchInput?.value) {
        this.#startPlaceholderRotation();
      }
    });
  };

  #updateClearButton = () => {
    if (!this.refs.clearButton) return;
    const isFocused = document.activeElement === this.refs.searchInput;
    this.refs.clearButton.hidden = !isFocused;
  };

  #showSuggestions = () => {
    if (!this.refs.suggestionsPanel || this.refs.suggestionsPanel.hasAttribute('data-empty')) return;
    this.refs.suggestionsPanel.hidden = false;
    this.refs.searchInput?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.#updateCollectionNav());
  };

  #hideSuggestions = () => {
    if (!this.refs.suggestionsPanel) return;
    this.refs.suggestionsPanel.hidden = true;
    if (!this.refs.predictiveSearchResults?.children.length) {
      this.refs.searchInput?.setAttribute('aria-expanded', 'false');
    }
  };

  #syncPanels = () => {
    const query = this.refs.searchInput?.value.trim() || '';

    if (query.length >= 2) {
      this.#hideSuggestions();
      return;
    }

    this.#clearResults();
    this.#showSuggestions();
  };

  #handleInput = () => {
    const hasValue = Boolean(this.refs.searchInput?.value.trim());
    this.toggleAttribute('has-value', hasValue);
    this.#updateClearButton();

    if (hasValue) {
      this.#stopPlaceholderRotation();
    }

    this.#syncPanels();
  };

  #preventBlur = (event) => {
    event.preventDefault();
  };

  #handleClearClick = (event) => {
    event.stopPropagation();
    this.#resetSearch();
    this.refs.searchInput?.blur();
    this.#updateClearButton();
    this.#hideSuggestions();
    this.#startPlaceholderRotation();
  };

  #handleSuggestionClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-suggestion]') : null;
    if (!button) return;

    const term = button.getAttribute('data-suggestion');
    if (!term || !this.refs.searchInput) return;

    this.refs.searchInput.value = term;
    this.toggleAttribute('has-value', true);
    this.#hideSuggestions();
    this.#submitSearch();
  };

  #scrollCollection = (direction) => {
    const track = this.refs.collectionTrack;
    const button = direction < 0 ? this.refs.collectionPrev : this.refs.collectionNext;
    if (!track || button?.disabled) return;

    const amount = Math.max(160, Math.round(track.clientWidth * 0.7));
    track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    // Smooth scroll updates mid-animation
    window.requestAnimationFrame(() => this.#updateCollectionNav());
    window.setTimeout(() => this.#updateCollectionNav(), 350);
  };

  #updateCollectionNav = () => {
    const track = this.refs.collectionTrack;
    const prev = this.refs.collectionPrev;
    const next = this.refs.collectionNext;
    if (!track || !prev || !next) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= maxScroll - 2;
    const noOverflow = maxScroll <= 2;

    prev.disabled = noOverflow || atStart;
    next.disabled = noOverflow || atEnd;
    prev.setAttribute('aria-disabled', String(prev.disabled));
    next.setAttribute('aria-disabled', String(next.disabled));
  };

  #handleClickOutside = (event) => {
    if (!this.contains(event.target)) {
      this.#clearResults();
      this.#hideSuggestions();
    }
  };

  #handleEscapeKey = (event) => {
    if (event.key === 'Escape') {
      this.#clearResults();
      this.#hideSuggestions();
      this.refs.searchInput?.blur();
      this.#updateClearButton();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Enter' && this.refs.searchInput.value.trim()) {
      event.preventDefault();
      this.#submitSearch();
    }
  };

  #handleSearch = async () => {
    const query = this.refs.searchInput?.value.trim() || '';

    if (query.length < 2) {
      this.#clearResults();
      this.#syncPanels();
      return;
    }

    this.#hideSuggestions();
    await this.#fetchSearchResults(query);
  };

  #fetchSearchResults = async (query) => {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    try {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[limit_scope]', 'each');

      const resultsMarkup = await sectionRenderer.getSectionHTML(this.#sectionId, false, url);
      if (signal.aborted || !resultsMarkup || !this.refs.predictiveSearchResults) return;

      await morph(this.refs.predictiveSearchResults, resultsMarkup, {
        duration: prefersReducedMotion() ? 0 : 200,
      });
      this.#updateResultsAccessibility();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
      }
    }
  };

  #updateResultsAccessibility = () => {
    if (!this.refs.searchInput || !this.refs.predictiveSearchResults) return;

    const hasResults = this.refs.predictiveSearchResults.children.length > 0;
    this.refs.searchInput.setAttribute('aria-expanded', hasResults ? 'true' : 'false');
    this.refs.predictiveSearchResults.style.display = hasResults ? 'block' : 'none';
  };

  #resetSearch = () => {
    if (this.refs.searchInput) {
      this.refs.searchInput.value = '';
    }
    this.removeAttribute('has-value');
    if (this.refs.clearButton) {
      this.refs.clearButton.hidden = true;
    }
    this.#clearResults();
  };

  #clearResults = () => {
    if (this.refs.predictiveSearchResults) {
      this.refs.predictiveSearchResults.innerHTML = '';
      this.refs.predictiveSearchResults.style.display = 'none';
    }
    this.#updateResultsAccessibility();
  };

  #submitSearch = () => {
    const query = this.refs.searchInput?.value.trim();
    if (query) {
      window.location.href = `${Theme.routes.search_url}?q=${encodeURIComponent(query)}&type=product`;
    }
  };
}

customElements.define('inline-search', InlineSearchComponent);
